import Bugsnag from '@bugsnag/js';
import { FormProvider } from 'react-hook-form';
import { AuthWrapper, InputWrapper, StyledPassword, StyledPhone } from 'components/auth_flow/SharedComponents';
import BirthdateFields from 'components/forms/BirthdateFields';
import { Text } from 'components/primitives/typography';
import useFormWithErrorTracking from 'hooks/useFormWithErrorTracking';
import styled from 'styled-components';
import useModal from 'components/remodal/v2/useModal';
import PhoneExistsModal from 'components/auth/modals/PhoneExistsModal';
import { useRef, useState } from 'react';
import { checkForDuplicatePhoneError } from 'components/auth/utils';
import { Box } from 'components/primitives/box';
import { useRouter } from 'next/router';
import { useUpdatePhoneNumberV2Mutation } from 'components/auth/hooks/authFlowHooks';
import { useQueryClient } from 'react-query';
import Input from 'components/ui/Input/Input';
import ErrorMessage from 'components/ui/ErrorMessage/ErrorMessage';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import Button from 'components/ui/Button/Button';
import useLazySnackbar from 'components/banners/useLazySnackbar';
import boatsetterPaths from 'utils/boatsetterPaths';

interface Props {
  hiddenFields?: string[];
  processSubmit: (values: unknown) => Promise<unknown>;
  processing: boolean;
  ctaText: string;
  isSignup?: boolean;
  defaultValues: SignupFormValues;
  disabledFields?: string[];
}

export interface SignupFormValues {
  first_name?: string;
  last_name?: string;
  email?: string;
  birthdate?: string;
  cell_number?: string;
  password?: string;
  privacy_policy?: boolean;
  sms_marketing?: boolean;
}

export default function SignupForm({
  hiddenFields = [],
  disabledFields = [],
  processSubmit,
  processing,
  isSignup = false,
  ctaText,
  defaultValues,
}: Props) {
  const { enqueueSnackbar } = useLazySnackbar();
  const methods = useFormWithErrorTracking<SignupFormValues>({
    mode: 'onChange',
    defaultValues,
  });

  const router = useRouter();
  const phoneRef = useRef<HTMLDivElement>(null);
  const [modalMessage, setModalMessage] = useState('');

  const { modal, openModal } = useModal(
    <PhoneExistsModal
      phoneRef={phoneRef?.current?.querySelector('input[name="cell_number"]')}
      censoredEmail={modalMessage}
    />,
    {
      size: 'small',
      trackingName: 'phone_exists_modal',
    }
  );

  const updatePhoneMutation = useUpdatePhoneNumberV2Mutation();
  const queryClient = useQueryClient();
  const internalProcessSubmit = async (values: SignupFormValues) => {
    await processSubmit(values);

    if (isSignup) return;

    if (values.cell_number) {
      try {
        await updatePhoneMutation.mutateAsync({
          phone: values.cell_number,
        });

        await queryClient.invalidateQueries('userDetails');
        await router.push({
          pathname: `${boatsetterPaths.signUp}/phone`,
          query: router.query,
        });
      } catch (e) {
        Bugsnag.notify(e);
        if (checkForDuplicatePhoneError(e.response)) {
          const email = e.response?.data?.resource_errors?.base?.[0]?.match(/\[(?<email>[\w\W]+)]/)?.groups?.email;
          setModalMessage(email);
          if (email) openModal();
        } else {
          enqueueSnackbar(e.response?.data?.resource_errors || 'Something went wrong. Please try again.', {
            variant: 'error',
            autoHideDuration: 5000,
          });
        }
        console.error(e);
      }
    }
  };

  const { handleSubmit, register, errors, watch } = methods;

  return (
    <AuthWrapper>
      {modal}
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(internalProcessSubmit)} data-testid="sign-up-form">
          {!hiddenFields.includes('first_name') && (
            <InputWrapper>
              <Input
                hasValue={!!watch('first_name')}
                hasError={!!errors.first_name}
                name="first_name"
                inputRef={register({
                  required: 'Please enter first name',
                })}
                placeholder="First name"
                disabled={disabledFields.includes('first_name')}
                data-testid="signup-first-name-input"
              />
              <ErrorMessage error={errors?.first_name} />
            </InputWrapper>
          )}

          {!hiddenFields.includes('last_name') && (
            <InputWrapper>
              <Input
                hasValue={!!watch('last_name')}
                hasError={!!errors.last_name}
                name="last_name"
                inputRef={register({
                  required: 'Please enter last name',
                })}
                placeholder="Last name"
                disabled={disabledFields.includes('last_name')}
                data-testid="signup-last-name-input"
              />
              <ErrorMessage error={errors?.last_name} />
            </InputWrapper>
          )}

          {!hiddenFields.includes('email') && (
            <InputWrapper>
              <Input
                hasError={!!errors.email}
                hasValue={!!watch('email')}
                placeholder="Email"
                type="email"
                name="email"
                inputRef={register({
                  required: 'Please enter email address',
                  // pattern: {
                  //   value: /^[A-Z0-9._+%]+@[A-Z0-9.-]+\.[A-Z]{2,20}$/i,
                  //   message: 'Invalid email address',
                  // },
                })}
                disabled={disabledFields.includes('email')}
                data-testid="signup-email-input"
              />
              <ErrorMessage error={errors?.email} />
            </InputWrapper>
          )}

          {!hiddenFields.includes('password') && (
            <InputWrapper>
              <StyledPassword name="password" register={register} />
              <ErrorMessage error={errors?.password} />
            </InputWrapper>
          )}

          {!hiddenFields.includes('cell_number') && (
            <Box pb="20px" ref={phoneRef}>
              <StyledPhone name="cell_number" disabled={disabledFields.includes('cell_number')} />
            </Box>
          )}

          {!hiddenFields.includes('birthdate') && (
            <InputWrapper className="birthDay">
              <BirthdateFields />
              <Text fSize="13px" pt="5px">
                You must be at least 18 years old to sign up. Other Boatsetter users won’t see your birthday.
              </Text>
            </InputWrapper>
          )}

          {!hiddenFields.includes('terms') && (
            <InputWrapper className="checkBox">
              <Checkbox
                id="terms"
                inputRef={register({
                  required: 'You must agree to the Terms and Conditions',
                })}
                name="privacy_policy"
                data-testid="privacy-policy-checkbox" // TODO: Find out why I cannot add this attribute. (Same applies to CTA buttons)
                label={
                  <span className="smallText">
                    I agree to the{' '}
                    <a target="_blank" href="/legal/terms_of_service" data-testid="terms-of-service-link">
                      Terms of service
                    </a>{' '}
                    and{' '}
                    <a target="_blank" href="/legal/privacy_policy" data-testid="privacy-policy-link">
                      Privacy policy
                    </a>
                    .
                  </span>
                }
              />
              <ErrorMessage error={errors?.privacy_policy} />
            </InputWrapper>
          )}

          {!hiddenFields.includes('sms_marketing') && (
            <InputWrapper className="checkBox">
              <Checkbox
                id="marketing"
                inputRef={register({})}
                name="sms_marketing"
                data-testid="sms-marking-checkbox"
                label={
                  <span className="smallText">
                    I consent to receive promotional marketing materials via an automated SMS text messaging system to
                    the telephone number I have provided for my account. Consent is not a condition to register for an
                    account or purchase any services. View Boatsetter’s{' '}
                    <a
                      target="_blank"
                      href="/legal/terms_of_service#sms_terms_of_service"
                      data-testid="terms-of-service-link"
                    >
                      SMS Texting Policy
                    </a>
                    .
                  </span>
                }
              />
              <ErrorMessage error={errors?.sms_marketing} />
            </InputWrapper>
          )}

          <Button
            type="submit"
            disabled={processing || updatePhoneMutation.isLoading}
            loading={processing || updatePhoneMutation.isLoading}
            text={ctaText}
            fullWidth
          />
        </Form>
      </FormProvider>
    </AuthWrapper>
  );
}
const Form = styled.form`
  display: flex;
  flex-flow: column;
`;
